import React from "react";
import { Box, Chip, IconButton, Link, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { navigate } from "../../Layout/Router";
import { subscribePoll } from "../../Layout/Poller";
import { DataTable, dur } from "../../Layout/Common";

// What the Game Servers pages share (Overview, Machines, Worlds, Sessions, Scaling, Costs, Regions, Providers, Fleets,
// Machine Specs, Logs): the `servers` admin group (contract: Docs/GameServers.md §11, §12-§16), the polling loader, the
// network regions (gsRegions), the state chips, times, ids and the events table. Pure helpers and small components;
// every page keeps its own state and dialogs.

export const gs = api.group("servers");
export const POLL_MS = 5000;
export const UPLOAD_TIMEOUT = 300000;   // the launcher zip goes to the private CDN
// The owner's rule: fixed layout and an explicit width on every column
export const FIXED = { "& table": { tableLayout: "fixed" }, "& .MuiTableCell-root": { px: 1, py: 0.75, fontSize: 13 } };
export const ELLIPSIS = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
export const MONO = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 };

// ---------- roles: `#servers` reads, `servers` acts ----------
const titleRoles = () => {
    const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
    return Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
};
export const canEdit = () => !!globals.loginData?.titles?.isMaster || titleRoles().includes("servers");
// GameLauncher.zip is ONE file for every title's machines: replacing it takes `servers` AND the core `admin` role on the title
export const canUploadLauncher = () => !!globals.loginData?.titles?.isMaster || (titleRoles().includes("servers") && titleRoles().includes("admin"));
// The network regions (gsRegions) are global, so editing them takes `servers` AND `admin` as well (§16)
export const canEditRegions = canUploadLauncher;
// A list filter id the backend accepts (it refuses anything else)
export const isId = (v) => /^[0-9a-f]{24}$/i.test(v ?? "");

// ---------- polling ----------
// One page's polled load: never overlapped (a load asked for while one runs re-runs once it ends), an answer for an
// older filter or another title is dropped, and a failed poll keeps the last data and only flags it stale.
// `fetch()` resolves to the state patch; `reload()` is for a filter change (it drops the answer in flight).
export class Loader {
    constructor(page, fetch, ms = POLL_MS) { this.page = page; this.fetch = fetch; this.ms = ms; this.gen = 0; }
    start() { this.alive = true; this.load(); if (this.ms) this.unsub = subscribePoll(this.load, this.ms); }
    stop() { this.alive = false; if (this.unsub) this.unsub(); }
    reload = () => { this.gen++; return this.load(); };
    load = async () => {
        if (this.running) { this.pending = true; return true; }
        this.running = true;
        const gen = this.gen, title = globals.selectedTitle;
        try {
            const patch = await this.fetch();
            if (this.alive && gen === this.gen && title === globals.selectedTitle && patch)
                this.page.setState({ ...patch, loadedAt: Date.now(), stale: null });
            return true;
        } catch (e) {
            if (this.alive && gen === this.gen) this.page.setState({ stale: e?.message || "failed" });
            return false;
        } finally {
            this.running = false;
            if (this.pending && this.alive) { this.pending = false; this.load(); }
        }
    };
}

// "updated 3 s ago", or a warning when the last refresh failed (the data on screen is the last good one)
export function StaleHint({ loadedAt, stale }) {
    if (stale) return <Tooltip title={`Refresh failing: ${stale}. The page keeps the last data and retries.`}>
        <Chip size="small" color="warning" variant="outlined" label={loadedAt ? `stale · ${ago(loadedAt)}` : "not loaded"} />
    </Tooltip>;
    return loadedAt ? <Typography variant="caption" color="text.secondary">updated {ago(loadedAt)}</Typography> : null;
}

// ---------- options (Vultr regions + plans, profiles, branches, builds): read once per title, 10 min ----------
let optionsCache = { title: null, at: 0, promise: null };
export function loadOptions(force = false) {
    const title = globals.selectedTitle;
    if (!force && optionsCache.title === title && optionsCache.promise && Date.now() - optionsCache.at < 600000) return optionsCache.promise;
    const promise = gs("Loading options", ["options"], { silent: true });
    optionsCache = { title, at: Date.now(), promise };
    promise.catch(() => { if (optionsCache.promise === promise) optionsCache = { title: null, at: 0, promise: null }; });
    return promise;
}
// A page's `options` state: loaded on mount and retried by its polled load until it is there (a failed read is not cached)
export const ensureOptions = (page) => {
    if (page.state.options || page.optionsLoading) return;
    page.optionsLoading = true;
    loadOptions().then(options => { if (options) page.setState({ options, optionsError: null }); })
        .catch(e => page.setState({ optionsError: e?.message || "failed" }))
        .finally(() => { page.optionsLoading = false; });
};
// ---------- network regions (gsRegions, §12 / §16): `region-list`, read once per title, 10 min ----------
// The answer { regions: [Region], titleRegions, defaultRegion }; when `region-list` fails, `options.netRegions` stands in.
let regionsCache = { title: null, at: 0, promise: null };
export function loadRegions(force = false) {
    const title = globals.selectedTitle;
    if (!force && regionsCache.title === title && regionsCache.promise && Date.now() - regionsCache.at < 600000) return regionsCache.promise;
    const promise = gs("Loading regions", ["region-list"], { silent: true })
        .catch(e => loadOptions().then(o => {
            if (!Array.isArray(o?.netRegions)) throw e;
            return { regions: o.netRegions, titleRegions: null, defaultRegion: o.defaultRegion };
        }))
        .then(r => ({ regions: r?.regions ?? [], titleRegions: r?.titleRegions ?? null, defaultRegion: r?.defaultRegion ?? null }));
    regionsCache = { title, at: Date.now(), promise };
    promise.catch(() => { if (regionsCache.promise === promise) regionsCache = { title: null, at: 0, promise: null }; });
    return promise;
}
// A region edit (Regions page) hands its answer here, so every page reads the new rows
export const setRegionsCache = (answer) => {
    regionsCache = { title: globals.selectedTitle, at: Date.now(), promise: Promise.resolve({ regions: answer?.regions ?? [], titleRegions: answer?.titleRegions ?? null, defaultRegion: answer?.defaultRegion ?? null }) };
};
// A page's `netRegions` state, like ensureOptions (retried by its polled load until it is there)
export const ensureRegions = (page) => {
    if (page.state.netRegions || page.regionsLoading) return;
    page.regionsLoading = true;
    loadRegions().then(netRegions => page.setState({ netRegions, regionsError: null }))
        .catch(e => page.setState({ regionsError: e?.message || "failed" }))
        .finally(() => { page.regionsLoading = false; });
};
const regionList = (nr) => (Array.isArray(nr) ? nr : nr?.regions ?? []);
export const findRegion = (id, nr) => regionList(nr).find(r => r.id === id) ?? null;
export const locationText = (loc) => (loc ? `${loc.provider}:${loc.code}` : "—");
// "br · Brazil"; a bare provider code of a row not migrated yet ("sao") names the region that holds it
export const regionLabel = (id, nr) => {
    if (id === "local") return "local (a developer's launcher)";
    if (!id) return "—";
    const r = findRegion(id, nr);
    if (r) return `${id} · ${r.name}${r.enabled === false ? " (disabled)" : ""}`;
    const holder = regionList(nr).find(x => (x.locations ?? []).some(l => l.code === id));
    return holder ? `${id} · old location code (${holder.id}: run regions-migrate)` : id;
};
// Select options: the regions (enabled first), then `extra` ids not in the table (local, an old value)
export const regionOptions = (nr, extra = [], { enabledOnly = false } = {}) => {
    const ids = new Set();
    const out = [];
    const list = [...regionList(nr)].sort((a, b) => (a.enabled === false) - (b.enabled === false) || String(a.id).localeCompare(String(b.id)));
    for (const r of list) {
        if (enabledOnly && r.enabled === false) continue;
        ids.add(r.id);
        const where = (r.locations ?? []).map(locationText).join(", ");
        out.push({ value: r.id, label: `${r.id} · ${r.name}${where ? ` (${where})` : ""}${r.enabled === false ? " · disabled" : ""}` });
    }
    for (const id of extra) if (id && !ids.has(id)) { ids.add(id); out.push({ value: id, label: regionLabel(id, nr) }); }
    return out;
};
// The regions a title plays in: its GameServersConfig.regions, else every enabled region
export const titleRegionIds = (nr) => (nr?.titleRegions?.length ? nr.titleRegions : regionList(nr).filter(r => r.enabled !== false).map(r => r.id));
export const planText = (p) => p
    ? `${p.vcpu} vCPU · ${p.ramMb >= 1024 ? `${+(p.ramMb / 1024).toFixed(1)} GB` : `${p.ramMb} MB`} RAM · ${p.diskGb} GB · $${Number(p.monthlyUsd ?? 0).toFixed(2)}/mo ($${Number(p.hourlyUsd ?? 0).toFixed(3)}/h)`
    : "";

// ---------- states ----------
const MACHINE = { provisioning: "info", "starting-up": "info", ready: "success", "shutting-down": "warning", stopped: "default",
                  updating: "secondary", lost: "error", "destroy-failed": "error" };
const SERVER = { planned: "default", downloading: "info", starting: "info", ready: "success", draining: "warning", stopping: "warning",
                 stopped: "default", crashed: "error", failed: "error" };
const SESSION = { allocating: "info", ready: "primary", active: "success", ended: "default", failed: "error", lost: "error" };
export const STATES = {
    machine: Object.keys(MACHINE), server: Object.keys(SERVER), session: Object.keys(SESSION),
};
// The scaler's machine drain phases (§14 autoDrain): soft = no new sessions / rooms, hard = players hop at a town,
// force = the rest hop after a notice
const DRAIN_PHASE = { soft: "info", hard: "warning", force: "error" };
const DRAIN_TEXT = {
    soft: "Scale-down, soft drain: no new sessions or rooms here; joins prefer other servers",
    hard: "Scale-down, hard drain: players reaching a safe town cell hop to the same area on another server",
    force: "Scale-down, forced drain: the remaining players got a notice and hop to another server",
};
export function AutoDrainChip({ autoDrain }) {
    if (!autoDrain?.phase) return null;
    const p = autoDrain.phase;
    return <Tooltip title={`${DRAIN_TEXT[p] ?? p}${autoDrain.at ? ` · since ${when(autoDrain.at)}` : ""}`}>
        <Chip size="small" color={DRAIN_PHASE[p] ?? "default"} variant="outlined" label={`drain ${p}`} />
    </Tooltip>;
}
const COLORS = { machine: MACHINE, server: SERVER, session: SESSION };
export function StateChip({ kind, state, title }) {
    const color = COLORS[kind]?.[state] ?? "default";
    const chip = <Chip size="small" color={color} variant={color === "default" ? "outlined" : "filled"} label={state ?? "?"} />;
    return title ? <Tooltip title={title}><span>{chip}</span></Tooltip> : chip;
}
// "machines: 2 ready · 1 lost" as chips, in the order of the state list
export function StateCounts({ kind, counts }) {
    const entries = Object.entries(counts ?? {}).filter(([, n]) => n > 0);
    if (entries.length === 0) return <Typography variant="body2" color="text.secondary">none</Typography>;
    const order = STATES[kind] ?? [];
    entries.sort((a, b) => (order.indexOf(a[0]) + 1 || 99) - (order.indexOf(b[0]) + 1 || 99));
    return <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {entries.map(([s, n]) => {
            const color = COLORS[kind]?.[s] ?? "default";
            return <Chip key={s} size="small" color={color} variant={color === "default" ? "outlined" : "filled"} label={`${s} ${n}`} />;
        })}
    </Box>;
}

// ---------- times ----------
// The backend answers UTC; a date without a zone designator is UTC too
export const asDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    const s = String(v);
    const d = new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(s) ? s : s + "Z");
    return isNaN(d.getTime()) ? null : d;
};
export const ageSec = (v, now = Date.now()) => { const d = asDate(v); return d ? Math.max(0, (now - d.getTime()) / 1000) : null; };
// "4 s ago", "3 min ago", "2 h ago", "5 d ago"; "—" when never
export const ago = (v, now = Date.now()) => {
    const s = ageSec(v, now);
    if (s == null) return "—";
    if (s < 60) return `${Math.floor(s)} s ago`;
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    return `${Math.floor(s / 86400)} d ago`;
};
export const when = (v) => { const d = asDate(v); return d ? d.toLocaleString() : "—"; };
export const fromUnix = (sec) => (sec > 0 ? new Date(sec * 1000) : null);
export const uptime = (startedAtSec) => (startedAtSec > 0 ? dur(Date.now() / 1000 - startedAtSec) : "—");
// A datetime-local input value (local time) <-> unix seconds
export const toLocalInput = (d) => {
    const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return x.toISOString().slice(0, 16);
};
export const fromLocalInput = (v) => { if (!v) return 0; const d = new Date(v); return isNaN(d.getTime()) ? 0 : Math.floor(d.getTime() / 1000); };

// ---------- numbers, versions ----------
export const n = (v) => Number(v ?? 0).toLocaleString();
export const usdH = (v) => `$${Number(v ?? 0).toFixed(3)}/h`;
export const usdDay = (v) => `$${(Number(v ?? 0) * 24).toFixed(2)}/day`;
export const pct = (used, total) => (total > 0 ? Math.round((used / total) * 100) : 0);
const BRANCH_SHORT = { Production: "Prod", Beta: "Beta", Internal: "Int", Development: "Dev" };
export const branchShort = (b) => BRANCH_SHORT[b] ?? b ?? "?";
export const majorMinor = (versionText) => (versionText ?? "").split(".").slice(0, 2).join(".");
export const versionLabel = (branch, versionText) => `${branchShort(branch)} ${versionText ?? "?"}`;
export const shortId = (id) => (id ? `…${String(id).slice(-6)}` : "—");
export const mb = (v) => (v >= 1024 ? `${(v / 1024).toFixed(1)} GB` : `${v ?? 0} MB`);

// ---------- ids ----------
export const copy = (text) => { try { navigator.clipboard.writeText(text); } catch { /* no clipboard */ } };
// A 24-hex id in a fixed-width cell: shortened, the whole id on hover, a copy button; `onClick` makes it a link
export function IdText({ id, onClick, full = false }) {
    if (!id) return <Typography variant="body2" color="text.secondary">—</Typography>;
    const text = full ? id : shortId(id);
    return <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        <Tooltip title={id}>
            {onClick
                ? <Link component="button" underline="hover" onClick={onClick} sx={{ ...MONO, ...ELLIPSIS, textAlign: "left" }}>{text}</Link>
                : <Box component="span" sx={{ ...MONO, ...ELLIPSIS }}>{text}</Box>}
        </Tooltip>
        <Tooltip title="Copy id"><IconButton size="small" sx={{ p: 0.25, ml: 0.25 }} onClick={e => { e.stopPropagation(); copy(id); }}>
            <ContentCopyIcon sx={{ fontSize: 13 }} />
        </IconButton></Tooltip>
    </Box>;
}
// One-line text cut to its column, the whole value on hover
export const Cut = ({ text, sx }) => text
    ? <Tooltip title={text}><Box component="span" sx={{ display: "block", ...ELLIPSIS, ...sx }}>{text}</Box></Tooltip>
    : <Typography variant="body2" color="text.secondary">—</Typography>;
// A small progress bar under a "used / total" figure
export const UsageBar = ({ used, total }) => {
    const p = Math.min(100, pct(used, total));
    const color = p >= 90 ? "error.main" : p >= 70 ? "warning.main" : "success.main";
    return <Box sx={{ height: 4, bgcolor: "action.hover", borderRadius: 1, mt: 0.25 }}><Box sx={{ width: `${p}%`, height: 4, bgcolor: color, borderRadius: 1 }} /></Box>;
};

// ---------- cross links ----------
const go = (sub, deep) => navigate({ title: globals.selectedTitle, page: "servers", sub, deep });
export const openMachine = (machineId, serverId) => go("machines", serverId ? [machineId, serverId] : [machineId]);
export const openSession = (sessionId) => go("sessions", [sessionId]);
export const openLogs = (filter) => go("logs", Object.entries(filter ?? {}).filter(([, v]) => v).flat());
export const openPage = (sub) => go(sub, []);

// ---------- events (§11 Event) ----------
export const EVENT_KINDS = ["machine-deployed", "machine-registered", "machine-back", "machine-lost", "machine-provision-timeout",
    "machine-drain", "machine-undrain", "machine-shutdown", "machine-destroy", "machine-update", "machine-destroy-failed",
    "machine-destroyed", "server-planned", "server-ready", "server-crashed", "server-failed", "server-restarted", "server-drain",
    "server-stop", "server-replaced", "server-restart", "server-removed", "sessions-lost", "no-capacity", "rollout-stuck", "launcher-uploaded",
    "session-end", "world-created", "world-opened", "world-open-failed", "world-removed",
    "launcher-silent", "server-kept", "launcher-update-refused", "slots-recounted", "no-spare", "clock-skew",
    "scale-up", "scale-down", "scale-shadow", "scale-capped", "location-backoff", "patch-fallback", "machine-adopted", "machine-released"];
const EVENT_COLOR = {
    "machine-lost": "error", "machine-provision-timeout": "error", "machine-destroy-failed": "error", "server-crashed": "error",
    "server-failed": "error", "sessions-lost": "error", "no-capacity": "error", "rollout-stuck": "error",
    "machine-drain": "warning", "machine-shutdown": "warning", "machine-destroy": "warning", "machine-update": "warning",
    "server-drain": "warning", "server-stop": "warning", "server-restart": "warning", "server-restarted": "warning", "server-removed": "default",
    "machine-registered": "success", "machine-back": "success", "server-ready": "success", "machine-undrain": "success",
    "machine-deployed": "info", "server-planned": "info", "server-replaced": "info", "launcher-uploaded": "info", "machine-destroyed": "default",
    "session-end": "warning", "world-created": "info", "world-opened": "success", "world-open-failed": "error", "world-removed": "warning",
    "launcher-silent": "warning", "launcher-update-refused": "error", "clock-skew": "warning", "no-spare": "warning",
    "scale-up": "info", "scale-down": "warning", "scale-shadow": "default", "scale-capped": "error", "location-backoff": "warning",
    "patch-fallback": "warning", "machine-adopted": "info", "machine-released": "warning",
};
export const EventKindChip = ({ kind }) => {
    const color = EVENT_COLOR[kind] ?? "default";
    return <Chip size="small" color={color} variant={color === "default" ? "outlined" : "filled"} label={kind} sx={{ maxWidth: "100%" }} />;
};
// The events timeline (Overview's latest, the Logs page): an id opens its machine / session
export function EventsTable({ events, compact, sx }) {
    const columns = [
        { id: "date", label: "Time", width: compact ? 95 : 150, render: e => <Tooltip title={when(e.date)}><span>{compact ? ago(e.date) : when(e.date)}</span></Tooltip> },
        { id: "kind", label: "Kind", width: 175, render: e => <EventKindChip kind={e.kind} /> },
        { id: "text", label: "Text", width: compact ? 500 : 470, render: e => <Typography variant="body2" sx={{ fontSize: 13, wordBreak: "break-word" }}>{e.text}</Typography> },
        { id: "machine", label: "Machine", width: 100, render: e => <IdText id={e.machineId} onClick={() => openMachine(e.machineId)} /> },
        { id: "server", label: "Server", width: 100, render: e => <IdText id={e.serverId} onClick={e.machineId ? () => openMachine(e.machineId, e.serverId) : () => openLogs({ server: e.serverId })} /> },
        { id: "session", label: "Session", width: 100, render: e => <IdText id={e.sessionId} onClick={() => openSession(e.sessionId)} /> },
    ];
    if ((events ?? []).length === 0) return <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>No events.</Typography>;
    return <DataTable columns={columns} rows={events} getRowKey={e => e.id} sx={{ ...FIXED, ...sx }} />;
}
